import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import moment from "moment";
import {
  ArrowRightIcon,
  ChevronRightIcon,
  CommentComposer,
  CommentIcon,
  CommentList,
  ExternalLinkIcon,
} from "../../StrategyCanvasHelpers";
import {
  STRATEGY_CANVAS_BRIEF,
  STRATEGY_CANVAS_LIVE_REVIEW_WAITING,
} from "../../../../../data/RouteUrls";
import useStrategyCanvasStore, {
  useStrategyCanvasStoreSelector,
} from "../../_redux/StrategyCanvasStore";
import useStrategyCanvasReviewStore, {
  useStrategyCanvasReviewStoreSelector,
} from "../_redux/StrategyCanvasReviewStore";
import {
  COMMENT_SCOPE,
  REVIEW_STATUS,
} from "../_redux/StrategyCanvasReviewUrls";
import CanvasBand, { CanvasGroup } from "../../CanvasBand/CanvasBand";
import {
  buildBands,
  useInitialCanvasFocus,
} from "../../StrategyCanvasStructure";
import SummaryGroups, { FilterChips } from "../../SummaryPanel/SummaryPanel";
import {
  ITEM_KIND,
  buildReviewSummary,
  countItems,
  countWhere,
  filterGroups,
} from "../../StrategyCanvasSummary";
import ReviewRow from "./ReviewRow";
import CompleteReviewModal from "../CompleteReviewModal/CompleteReviewModal";
import ActionConfirmModal from "../../StrategyCanvasLiveReviewWaiting/ActionConfirmModal/ActionConfirmModal";
import { StandardTooltip } from "../../../../shared/StandardTooltip/StandardTooltip";
import "./StrategyCanvasReviewMode.scss";

const selectReviewByUniqueId = (state) => state.reviewByUniqueId;
const selectReviewLoading = (state) => state.reviewLoading;
const selectCanvasStatusByUniqueId = (state) => state.canvasStatusByUniqueId;
const selectStartingLiveReview = (state) => state.startingLiveReview;

// Shimmer placeholder shown the first time a coach opens Review Mode, while
// GET /coach/strategy-canvas/:uniqueId/review (5.2) is still in flight -
// mirrors the real layout (sections of rows on the left, tabs/pills/cards
// on the right) instead of a blank "Loading..." screen.
const ReviewModeSkeleton = () => (
  <div className="scrm-container">
    <div className="scrm-left">
      {Array.from({ length: 3 }).map((_, sectionIndex) => (
        <div className="rm-section" key={sectionIndex}>
          <div className="rm-section-heading">
            <span className="rm-skel rm-skel-bar" />
            <span className="rm-skel rm-skel-title" />
          </div>
          <div className="rm-rows">
            {Array.from({ length: 4 }).map((__, rowIndex) => (
              <div className="rm-skel rm-skel-row" key={rowIndex} />
            ))}
          </div>
          <div className="rm-section-divider" />
        </div>
      ))}
    </div>

    <div className="scrm-right">
      <div className="rm-summary-header">
        <span className="rm-skel rm-skel-title" style={{ width: 120 }} />
      </div>
      <div className="rm-summary-header-divider" />
      <div className="rm-right-body">
        <div className="rm-summary-pills">
          <span className="rm-skel rm-skel-pill" />
          <span className="rm-skel rm-skel-pill" />
          <span className="rm-skel rm-skel-pill" />
        </div>
        {Array.from({ length: 3 }).map((_, index) => (
          <span className="rm-skel rm-skel-card" key={index} />
        ))}
      </div>
    </div>
  </div>
);

// Top-level "Overall Assessment" comment list + composer (section 5.4,
// scope_type: "overall"). Kept separate from the section version below
// since it has no heading/trigger row of its own - it's always expanded.
const OverallCommentPanel = ({
  comments,
  onAdd,
  onEdit,
  onDelete,
  placeholder,
  readOnly,
}) => {
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  // Collapsed until asked for. Most reviews are carried by the per-block
  // decisions, so an always-open textarea sat at the foot of every canvas
  // taking up height for something the coach usually skips.
  const [isCommenting, setIsCommenting] = useState(false);

  // CommentComposer only calls this once the draft has content, so there's no
  // empty case to guard here.
  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    const saved = await onAdd(draft.trim());
    setSaving(false);
    if (saved) {
      setDraft("");
      setIsCommenting(false);
    }
  };

  const handleDelete = async (commentId) => {
    setDeletingId(commentId);
    await onDelete(commentId);
    setDeletingId(null);
  };

  // Only one overall comment at a time - the trigger and composer both stay
  // hidden once one exists; deleting it frees the slot back up.
  const showTrigger = !readOnly && comments.length === 0 && !isCommenting;

  return (
    <>
      {showTrigger && (
        <button
          type="button"
          className="rm-overall-trigger"
          onClick={() => setIsCommenting(true)}
        >
          <CommentIcon size={12} />
          Add your overall assessment for the CEO
        </button>
      )}

      {comments.length > 0 && (
        <CommentList
          comments={comments}
          onDelete={handleDelete}
          onEdit={onEdit}
          deletingId={deletingId}
          readOnly={readOnly}
          // Nothing above this card names it now that the section heading is
          // gone, so the card has to say what it is itself.
          badge="Overall assessment"
        />
      )}

      {!readOnly && comments.length === 0 && isCommenting && (
        <CommentComposer
          className="rm-section-comment-composer"
          value={draft}
          onChange={setDraft}
          onSave={handleSave}
          saving={saving}
          placeholder={placeholder}
        />
      )}
    </>
  );
};

// Section heading + its comment panel, matching Figma node 33288-145131:
// the colored bar and section title sit on one row (not after the
// accordion rows). While the accordion is closed, only the "Add a comment
// for CEO" trigger shows on the right. Once opened, the "section comment
// (optional)" label appears right after the title AND the trigger stays on
// the right of that same heading row - then the panel (existing comments,
// if any, plus a fresh textarea + Save comment button) expands directly
// below, before the block rows.
const SectionCommentHeading = ({
  layer,
  briefId,
  readOnly,
  showTitle = true,
  hint,
}) => {
  const [isCommenting, setIsCommenting] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const comments = layer.section_comments || [];
  const showTrigger = !readOnly && comments.length === 0;

  // CommentComposer handles the empty-draft case before calling this.
  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    const saved = await useStrategyCanvasReviewStore
      .getState()
      .addComment(briefId, {
        scopeType: COMMENT_SCOPE.SECTION,
        scopeKey: layer.layer,
        commentText: draft.trim(),
      });
    setSaving(false);
    if (saved) {
      setDraft("");
      setIsCommenting(false);
    }
  };

  const handleDelete = async (commentId) => {
    setDeletingId(commentId);
    await useStrategyCanvasReviewStore
      .getState()
      .deleteComment(briefId, commentId);
    setDeletingId(null);
  };

  const handleEdit = async (commentId, commentText) =>
    useStrategyCanvasReviewStore
      .getState()
      .editComment(briefId, commentId, commentText);

  return (
    <>
      {/* Bands whose label already names the section (Growth / Core / DNA)
          pass showTitle={false}; the row then carries only the comment
          affordance, and collapses away entirely when there isn't one. */}
      {(showTitle || showTrigger || isCommenting) && (
        <div
          className={`rm-section-heading${
            showTitle ? "" : " rm-section-heading--bare"
          }`}
        >
          {showTitle && (
            <>
              <span className="rm-section-bar" />
              <span className="rm-section-title">{layer.layer}</span>
              {hint && <span className="rm-section-hint">{hint}</span>}
            </>
          )}
          {isCommenting && (
            <span className="rm-section-heading-meta">
              section comment (optional)
            </span>
          )}
          {showTrigger && (
            <button
              className="rm-comment-link rm-section-comment-trigger"
              onClick={() => setIsCommenting((prev) => !prev)}
            >
              <CommentIcon size={12} />
              Add a comment for the CEO
            </button>
          )}
        </div>
      )}

      {(isCommenting || comments.length > 0) && (
        <div className="rm-section-comment-panel">
          {comments.length > 0 && (
            <CommentList
              comments={comments}
              onDelete={handleDelete}
              onEdit={handleEdit}
              deletingId={deletingId}
              readOnly={readOnly}
              className="rm-section-comment-list"
            />
          )}

          {!readOnly && isCommenting && (
            <CommentComposer
              className="rm-section-comment-composer"
              value={draft}
              onChange={setDraft}
              onSave={handleSave}
              saving={saving}
              placeholder="Summarise your overall read on this section for the CEO…"
            />
          )}
        </div>
      )}
    </>
  );
};

// Footer CTA (Complete review & send to CEO / Start Live Review) - when
// disabled, wraps itself in a tooltip explaining why, instead of just
// sitting there unusable with no explanation.
const FooterActionButton = ({ label, onClick, disabled, disabledReason }) => {
  const button = (
    <button
      type="button"
      className="rm-complete-btn"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
    >
      {label}
      <ArrowRightIcon />
    </button>
  );

  if (!disabled || !disabledReason) return button;

  return (
    <StandardTooltip tooltTipContent={disabledReason}>
      <span className="rm-btn-tooltip-wrap">{button}</span>
    </StandardTooltip>
  );
};

// Review Mode - left column renders the real 5.2 payload (layers -> blocks,
// each an expandable ReviewRow with Accept / To-discuss / comments). Right
// column is the Review Summary panel, built on the FE from the same
// layers[].blocks[] data per the doc's explicit instructions (there's no
// separate flat-feed endpoint).
const StrategyCanvasReviewMode = ({ briefId }) => {
  const history = useHistory();
  // Review Summary filter — all / to discuss / with comments / section comments.
  const [summaryFilter, setSummaryFilter] = useState("all");
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showStartLiveReviewConfirm, setShowStartLiveReviewConfirm] =
    useState(false);

  const reviewByUniqueId = useStrategyCanvasReviewStoreSelector(
    selectReviewByUniqueId,
  );
  const reviewLoading =
    useStrategyCanvasReviewStoreSelector(selectReviewLoading);
  const review = reviewByUniqueId[briefId];

  const canvasStatusByUniqueId = useStrategyCanvasStoreSelector(
    selectCanvasStatusByUniqueId,
  );
  const canvasStatus = canvasStatusByUniqueId[briefId];
  const canvasState = canvasStatus?.canvas_state;
  const startingLiveReview = useStrategyCanvasReviewStoreSelector(
    selectStartingLiveReview,
  );

  // Once the review has been sent to the CEO (Start Live Review is now the
  // footer action), nothing here should be editable any more - no
  // accept/discuss, no adding/deleting comments. Everything already
  // decided still renders, just without the controls that call an API.
  const isSubmitted = canvasState === "COACH_REVIEW_SUBMITTED";

  useEffect(() => {
    useStrategyCanvasStore.getState().fetchCanvasStatus(briefId);
    // eslint-disable-next-line
  }, [briefId]);

  // The API returns 7 flat layers; the canvas groups them into 4 bands (see
  // StrategyCanvasStructure.js). Computed before the early return below so
  // the hook order stays stable across the loading/loaded switch.
  const bands = useMemo(() => buildBands(review?.layers), [review]);

  // Where the coach lands: Business DNA open with its first block expanded,
  // every other band folded away. Only one row's accordion is open at a time
  // across the whole canvas.
  const {
    collapsedBands,
    setCollapsedBands,
    expandedBlockKey,
    setExpandedBlockKey,
  } = useInitialCanvasFocus(bands);

  // ═══════════════════════════════════════════════════════════════════════════
  // The Complete Review gate — ported from Execution Blueprint (2026-09-03)
  //
  // "Complete review & send to CEO" used to be `disabled` until every block was
  // marked, with a `StandardTooltip` giving the score. That told the coach they
  // could not proceed but not WHICH of twenty-eight rows was holding them up,
  // and a tooltip can only ever give a count. Letting the click land means the
  // check can answer the actual question: take them to the first unmarked block
  // and mark all of them.
  //
  // ⚠️ Unmarked is `!block.review_status`, matching the per-band `reviewedCount`
  // that `buildBands` computes. Deliberately NOT derived from
  // `review.reviewed_count` / `total_blocks`: those are server-computed totals,
  // so they can say "3 remaining" without being able to say which three.
  const pendingBlocks = useMemo(() => {
    const out = [];
    // band → section → group → block, walked in order, so index 0 is the
    // topmost unmarked row on screen.
    bands.forEach((band) => {
      (band.sections || []).forEach((section) => {
        (section.groups || []).forEach((group) => {
          (group.blocks || []).forEach((block) => {
            if (!block.review_status) {
              out.push({ key: block.block_key, bandKey: band.key });
            }
          });
        });
      });
    });
    return out;
  }, [bands]);

  // The keys to redden and flash. Empty until the coach actually tries to
  // submit — see `ReviewRow`'s `pending` note for why that matters.
  const [flaggedPendingKeys, setFlaggedPendingKeys] = useState(() => new Set());
  const [pendingFlashKey, setPendingFlashKey] = useState(0);
  const reviewCanvasRef = useRef(null);
  // The keys this click flashes, frozen at click time. See the flash effect.
  const flashTargetsRef = useRef([]);

  // Scroll to the first flagged row and flash all of them.
  //
  // ⚠️ KEYED ON `pendingFlashKey` ALONE, and that is the point. An earlier
  // version of this effect in EXW also depended on the expanded/collapsed
  // state, which meant every expand or collapse after a blocked submit re-ran
  // it: the canvas jumped back to the first pending row and everything flashed
  // again while the coach was trying to work through the rows. Scroll and flash
  // are a response to one click and must happen exactly once per click.
  //
  // Those deps are not needed anyway — the `set*` calls in
  // `handleCompleteClick` batch into one render and effects run after it
  // commits, so the bands are already open and the rows already in the DOM by
  // the time this queries for them.
  //
  // The keys come from a REF rather than from `flaggedPendingKeys` for the same
  // reason: that set shrinks as the coach marks rows, and reading it here would
  // put it in the dep array and reintroduce the re-firing.
  useEffect(() => {
    if (!pendingFlashKey) return undefined;
    const root = reviewCanvasRef.current;
    if (!root) return undefined;

    const nodes = flashTargetsRef.current
      .map((key) => root.querySelector(`[data-review-target="block-${key}"]`))
      .filter(Boolean);
    if (nodes.length === 0) return undefined;

    nodes[0].scrollIntoView({ behavior: "smooth", block: "center" });
    nodes.forEach((node) => node.classList.add("rr-row-flash"));
    const timer = setTimeout(
      () => nodes.forEach((node) => node.classList.remove("rr-row-flash")),
      1600,
    );
    return () => clearTimeout(timer);
  }, [pendingFlashKey]);

  // Clear the flags as the coach works through them, so a row stops being red
  // the moment it is marked rather than staying red until the next submit.
  useEffect(() => {
    if (flaggedPendingKeys.size === 0) return;
    const stillPending = new Set(pendingBlocks.map((entry) => entry.key));
    const next = [...flaggedPendingKeys].filter((key) => stillPending.has(key));
    if (next.length !== flaggedPendingKeys.size) {
      setFlaggedPendingKeys(new Set(next));
    }
  }, [pendingBlocks, flaggedPendingKeys]);

  if (reviewLoading || !review) {
    return <ReviewModeSkeleton />;
  }

  // Clicking Complete Review. Either the review is finished and the confirm
  // dialog opens, or the coach is shown exactly what is missing.
  const handleCompleteClick = () => {
    if (pendingBlocks.length === 0) {
      setShowCompleteModal(true);
      return;
    }

    const keys = pendingBlocks.map((entry) => entry.key);
    setFlaggedPendingKeys(new Set(keys));
    // What this click flashes. A ref, so the effect can read it without taking
    // a dependency on state that changes as the coach works.
    flashTargetsRef.current = keys;

    // ⚠️ EVERY band holding a pending block opens, not just the first one's.
    // Opening only the first would leave the rest of the flagged rows reddened
    // and flashing inside collapsed bands, where the coach cannot see any of
    // it — so the count would say twelve and the screen would show one.
    const pendingBandKeys = new Set(pendingBlocks.map((e) => e.bandKey));
    setCollapsedBands((prev) => {
      const next = new Set(
        [...prev].filter((bandKey) => !pendingBandKeys.has(bandKey)),
      );
      // Same size means nothing needed opening. Returning `prev` keeps the
      // reference stable and skips a pointless re-render.
      return next.size === prev.size ? prev : next;
    });

    // Only the topmost pending row expands — `expandedBlockKey` holds one key,
    // so this is an accordion by construction. It is also the right one: it is
    // where the scroll lands.
    setExpandedBlockKey(pendingBlocks[0].key);

    // A counter rather than a boolean: clicking again with rows still pending
    // has to re-run the scroll and re-fire the flash, and a boolean already
    // `true` would do neither.
    setPendingFlashKey((key) => key + 1);
  };

  const toggleBand = (bandKey) =>
    setCollapsedBands((prev) => {
      const next = new Set(prev);
      if (next.has(bandKey)) next.delete(bandKey);
      else next.add(bandKey);
      return next;
    });

  const handleOpenCompanyBrief = () => {
    window.open(
      STRATEGY_CANVAS_BRIEF.replace(":briefId", briefId),
      "_blank",
      "noopener,noreferrer",
    );
  };

  // Review Summary, grouped in canvas order (band > section) rather than by
  // decision time. A section comment now sits at the head of its own section
  // instead of after every block, and the overall comment closes the list as
  // its own group - see StrategyCanvasSummary.js.
  const summaryGroups = buildReviewSummary(review);
  const summaryTotal = countItems(summaryGroups);
  const summaryDiscuss = countWhere(
    summaryGroups,
    (item) => item.status === REVIEW_STATUS.TO_DISCUSS,
  );
  const summaryWithComments = countWhere(
    summaryGroups,
    (item) => item.comments.length > 0,
  );
  const summarySectionComments = countWhere(
    summaryGroups,
    (item) => item.kind === ITEM_KIND.SECTION,
  );

  const SUMMARY_FILTERS = {
    all: () => true,
    discuss: (item) => item.status === REVIEW_STATUS.TO_DISCUSS,
    comments: (item) => item.comments.length > 0,
    sections: (item) => item.kind === ITEM_KIND.SECTION,
  };
  const visibleSummaryGroups = filterGroups(
    summaryGroups,
    SUMMARY_FILTERS[summaryFilter] || SUMMARY_FILTERS.all,
  );

  // Section 7.1 - "Send to CEO" in the Canvas Review Complete modal. On
  // success, close the modal and refresh the canvas status - once it comes
  // back as COACH_REVIEW_SUBMITTED the footer button below swaps to "Start
  // Live Review" in place. No redirect here; the coach stays on this screen.
  const handleSubmitReview = async (body) => {
    setSubmitting(true);
    const result = await useStrategyCanvasReviewStore
      .getState()
      .submitReview(briefId, body);
    setSubmitting(false);
    if (result) {
      setShowCompleteModal(false);
      useStrategyCanvasStore.getState().fetchCanvasStatus(briefId);
    }
  };

  // Section 7.3 - the same "Start Live Review" call used on the Live Review
  // waiting screen's own button. Only shown here once canvas_state is
  // COACH_REVIEW_SUBMITTED; on success, move on to that screen.
  const handleStartLiveReview = async () => {
    const result = await useStrategyCanvasReviewStore
      .getState()
      .startLiveReview(briefId);
    if (result) {
      history.push(
        STRATEGY_CANVAS_LIVE_REVIEW_WAITING.replace(":briefId", briefId),
      );
    }
  };

  const handleConfirmStartLiveReview = () => {
    setShowStartLiveReviewConfirm(false);
    handleStartLiveReview();
  };

  return (
    <div className="scrm-container">
      {/* `reviewCanvasRef` scopes the gate's `querySelector` to this panel, so
          it can only ever match rows — never something in the summary rail
          that happens to share the attribute. */}
      <div className="scrm-left" ref={reviewCanvasRef}>
        {bands.map((band) => (
          <CanvasBand
            key={band.key}
            label={band.label}
            color={band.color}
            reviewedCount={band.reviewedCount}
            totalCount={band.totalCount}
            expanded={!collapsedBands.has(band.key)}
            onToggle={() => toggleBand(band.key)}
          >
            {band.sections.map((section) => (
              <div className="rm-section" key={section.key}>
                <SectionCommentHeading
                  layer={section.layer}
                  briefId={briefId}
                  readOnly={isSubmitted}
                  showTitle={!section.hideHeading}
                  hint={section.hint}
                />

                <div className="cb-groups">
                  {section.groups.map((group, groupIndex) => (
                    <CanvasGroup
                      key={group.label || `group-${groupIndex}`}
                      label={group.label}
                    >
                      <div className="rm-rows">
                        {group.blocks.map((block) => (
                          <ReviewRow
                            key={block.block_key}
                            uniqueId={briefId}
                            block={block}
                            expanded={expandedBlockKey === block.block_key}
                            onToggleExpand={() =>
                              setExpandedBlockKey((prev) =>
                                prev === block.block_key
                                  ? null
                                  : block.block_key,
                              )
                            }
                            readOnly={isSubmitted}
                            pending={flaggedPendingKeys.has(block.block_key)}
                          />
                        ))}
                      </div>
                    </CanvasGroup>
                  ))}
                </div>
              </div>
            ))}
          </CanvasBand>
        ))}

        {/* No heading or card of its own: collapsed, this is a single link,
            and the link already names what it does. The subtitle that used to
            sit here repeated the composer's own placeholder word for word. */}
        <div className="rm-overall">
          <div className="rm-overall-comments">
            <OverallCommentPanel
              comments={review.overall_comments || []}
              placeholder="Your overall assessment of the Canvas for the CEO"
              readOnly={isSubmitted}
              onAdd={(text) =>
                useStrategyCanvasReviewStore.getState().addComment(briefId, {
                  scopeType: COMMENT_SCOPE.OVERALL,
                  scopeKey: null,
                  commentText: text,
                })
              }
              onEdit={(commentId, text) =>
                useStrategyCanvasReviewStore
                  .getState()
                  .editComment(briefId, commentId, text)
              }
              onDelete={(commentId) =>
                useStrategyCanvasReviewStore
                  .getState()
                  .deleteComment(briefId, commentId)
              }
            />
          </div>
        </div>
      </div>

      <div className="scrm-right">
        {/* No accepted/to-discuss counts here: the filter chips directly
            below already carry the same two numbers, and every item in the
            list under them states its own status. */}
        <div className="rm-summary-header">
          <span className="rm-summary-header-title">Review Summary</span>
          <button
            className="rm-company-brief-link"
            onClick={handleOpenCompanyBrief}
          >
            Company Brief
            <ExternalLinkIcon size={14} />
          </button>
        </div>
        <div className="rm-summary-header-divider" />

        {summaryTotal > 0 && (
          <div className="rm-summary-filters">
            <FilterChips
              value={summaryFilter}
              onChange={setSummaryFilter}
              options={[
                { key: "all", label: "All", count: summaryTotal },
                { key: "discuss", label: "To discuss", count: summaryDiscuss },
                {
                  key: "comments",
                  label: "With comments",
                  count: summaryWithComments,
                },
                {
                  key: "sections",
                  label: "Section comments",
                  count: summarySectionComments,
                },
              ]}
            />
          </div>
        )}

        <div className="rm-right-body">
          {summaryTotal === 0 ? (
            <div className="rm-summary-empty">
              Your review will appear here.
            </div>
          ) : (
            <SummaryGroups
              groups={visibleSummaryGroups}
              emptyLabel="Nothing matches this filter."
            />
          )}
        </div>

        <div className="rm-right-footer">
          {isSubmitted ? (
            <>
              <FooterActionButton
                label="Start live review"
                onClick={() => setShowStartLiveReviewConfirm(true)}
                disabled={startingLiveReview}
                disabledReason="Starting your live review session…"
              />
              {canvasStatus?.meeting_datetime && (
                <div className="rm-meeting-scheduled">
                  <span>You&apos;ve scheduled the live review for</span>
                  <strong>
                    {moment(canvasStatus.meeting_datetime).format(
                      "h:mm A [on] Do MMMM, YYYY.",
                    )}
                  </strong>
                </div>
              )}
            </>
          ) : (
            <>
              {/* ⚠️ NO `disabled` / `disabledReason`. The click is always
                  allowed and the check happens in `handleCompleteClick`,
                  which can point at the rows rather than only counting
                  them — see the gate's note above. */}
              <FooterActionButton
                label="Complete review & send to CEO"
                onClick={handleCompleteClick}
              />
              {/* Shown only after a blocked attempt, and it counts the FLAGGED
                  rows rather than `pendingBlocks` — so it drops as the coach
                  marks each one and disappears when the last is done, which is
                  the acknowledgement that they have cleared it.
                  `role="alert"` because it appears in response to an action and
                  is the only text explaining why nothing happened. */}
              {flaggedPendingKeys.size > 0 && (
                <p className="rm-pending-note" role="alert">
                  {/* Built as one string rather than interleaved JSX. The
                      inline version depended on JSX collapsing newlines around
                      each expression to produce "still needs" — correct, but
                      the kind of thing a reformat breaks silently. */}
                  {flaggedPendingKeys.size === 1
                    ? "1 block still needs a decision before this can go to the CEO."
                    : `${flaggedPendingKeys.size} blocks still need a decision before this can go to the CEO.`}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {showCompleteModal && (
        <CompleteReviewModal
          totalBlocks={review.total_blocks}
          acceptedCount={review.accepted_count}
          toDiscussCount={review.to_discuss_count}
          commentCount={review.comment_count}
          submitting={submitting}
          onCancel={() => setShowCompleteModal(false)}
          onSubmit={handleSubmitReview}
        />
      )}

      {showStartLiveReviewConfirm && (
        <ActionConfirmModal
          icon={<ChevronRightIcon size={20} />}
          title="Start the live review?"
          subtitle="This begins your session with the CEO and their leadership team."
          text="Make sure the CEO is with you. You'll finalise each block together, then lock the canvas. Locking can't be undone, and once you start, you can't go back to your solo review."
          confirmLabel="Start live review"
          onConfirm={handleConfirmStartLiveReview}
          onCancel={() => setShowStartLiveReviewConfirm(false)}
        />
      )}
    </div>
  );
};

export default StrategyCanvasReviewMode;
